import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
  DEFAULT_LOCALE,
  LOCALES,
  localizedPath,
  localeFromPath,
  pathWithoutLocale,
  textDirection,
} from '../i18n/config';
import { useLocale } from '../i18n/useLocale';
import { previewTags } from '../seo/preview';
import { SITE_URL } from '../site';

interface SeoProps {
  title: string;
  description?: string;
}

/** Title, description, canonical URL, hreflang alternates and link preview of a public page. */
export function Seo({ title, description }: SeoProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname) ?? DEFAULT_LOCALE;
  const path = pathWithoutLocale(pathname);
  const url = (lang: string) => `${SITE_URL}${localizedPath(lang, path)}`;

  return (
    <Helmet htmlAttributes={{ lang: locale, dir: textDirection(locale) }}>
      <title>{title}</title>
      {description !== undefined && <meta name="description" content={description} />}
      <link rel="canonical" href={url(locale)} />
      {LOCALES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url(lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url(DEFAULT_LOCALE)} />
      {previewTags({
        locale,
        title,
        description,
        url: url(locale),
        imageAlt: t('landing.hero.title'),
      }).map((tag) => (
        // Helmet drops a meta whose name is present but undefined, so pass only the one it has.
        <meta
          key={`${tag.property ?? tag.name ?? ''}=${tag.content}`}
          {...(tag.property === undefined ? { name: tag.name } : { property: tag.property })}
          content={tag.content}
        />
      ))}
    </Helmet>
  );
}

/** For private pages (dashboard, admin): never indexed. nginx also sends X-Robots-Tag. */
export function NoIndex({ title }: { title: string }) {
  const locale = useLocale();
  return (
    <Helmet htmlAttributes={{ lang: locale, dir: textDirection(locale) }}>
      <title>{title}</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}
